import type { DownloadView } from '@zyx/shared';
import { FileDown, RotateCcw, Square, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/use-connection';
import { type DownloadAction, useDownloadAction } from '@/hooks/use-downloads';
import { formatBytes } from '@/lib/crawl-labels';
import { editionLabel, formatRuntime, qualityLabel } from '@/lib/format';
import { urls } from '@/lib/urls';
import { cn } from '@/lib/utils';
import { AssetImage } from './asset';

const STATUS_LABELS: Readonly<Record<DownloadView['status'], string>> = {
  queued: 'Waiting',
  downloading: 'Downloading',
  done: 'Ready offline',
  failed: 'Failed',
  cancelled: 'Stopped',
};

function Progress(props: { readonly download: DownloadView }) {
  const { segmentsDone, segmentsTotal, status } = props.download;
  const fraction = status === 'done' ? 1 : segmentsTotal === undefined || segmentsTotal === 0 ? 0 : segmentsDone / segmentsTotal;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(fraction * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full rounded-full', status === 'failed' ? 'bg-destructive' : status === 'done' ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${fraction * 100}%` }} />
    </div>
  );
}

/**
 * Downloads, one row each: what it is, how far it got, and what can be done with it.
 * `showMovie` adds the cover and code, for the page that lists every movie's.
 */
export function DownloadList(props: { readonly downloads: ReadonlyArray<DownloadView>; readonly showMovie?: boolean }) {
  const api = useApi();
  const act = useDownloadAction();
  const run = (downloadId: number, action: DownloadAction) => act.mutate({ downloadId, action });
  // A movie in several parts names the part in each saved file.
  const parts = (download: DownloadView) => props.downloads.some((other) => other.variantId === download.variantId && other.episodeNo !== download.episodeNo) || download.episodeNo > 1;

  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {props.downloads.map((download) => (
        <li key={download.downloadId} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
          {props.showMovie === true ? (
            <Link to={urls.movie(download.code)} className="w-20 shrink-0 overflow-hidden rounded">
              <AssetImage file={download.cover} alt={download.code} className="aspect-[3/2] w-full" />
            </Link>
          ) : null}
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
              {props.showMovie === true ? (
                <Link to={urls.movie(download.code)} className="font-mono font-semibold text-primary hover:underline">
                  {download.code}
                </Link>
              ) : null}
              <span>
                {editionLabel(download.edition)}
                {parts(download) ? ` · part ${download.episodeNo}` : ''} · {qualityLabel(download.resolution)}
              </span>
              <span className="text-xs text-muted-foreground">
                {STATUS_LABELS[download.status]}
                {download.bytes > 0 ? ` · ${formatBytes(download.bytes)}` : ''}
                {download.status === 'downloading' && download.segmentsTotal !== undefined ? ` · ${download.segmentsDone} of ${download.segmentsTotal} pieces` : ''}
                {download.status === 'done' && download.durationS !== undefined ? ` · ${formatRuntime(download.durationS) ?? ''}` : ''}
              </span>
            </p>
            {download.status === 'done' ? null : <Progress download={download} />}
            {download.error === undefined || download.status === 'done' ? null : <p className="text-xs text-amber-700 dark:text-amber-400">{download.error}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {download.status === 'done' ? (
              <Button asChild size="sm" variant="outline">
                <a href={api.downloadMp4Url(download.downloadId, parts(download))} download>
                  <FileDown />
                  Save MP4
                </a>
              </Button>
            ) : null}
            {download.status === 'queued' || download.status === 'downloading' ? (
              <Button size="icon-sm" variant="ghost" aria-label="Stop" title="Stop" disabled={act.isPending} onClick={() => run(download.downloadId, 'cancel')}>
                <Square />
              </Button>
            ) : null}
            {download.status === 'failed' || download.status === 'cancelled' ? (
              <Button size="icon-sm" variant="ghost" aria-label="Try again" title="Try again, keeping what was fetched" disabled={act.isPending} onClick={() => run(download.downloadId, 'retry')}>
                <RotateCcw />
              </Button>
            ) : null}
            <Button size="icon-sm" variant="ghost" aria-label="Delete" title="Delete this copy and its files" disabled={act.isPending} onClick={() => run(download.downloadId, 'delete')}>
              <Trash2 />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
